import React, { FC, useState, useEffect } from "react";
import { useMediaQuery } from "react-responsive";
import { useTranslation } from "react-i18next";

import MeasuredBy from "../molecules/MeasuredBy";
import { activeStylesContinue, resultScreenUI, uxGender } from "../../configs";

import { CheckCircle as CheckCircleIcon } from "@mui/icons-material";
import CheckIconUnavailable from "../../assets/icons/CheckIconUnavailable";

import H_twh from "../../assets/result/H_twh.svg";
import H_ctw from "../../assets/result/H_ctw.svg";
import H_ctwh from "../../assets/result/H_ctwh.svg";
import H_h from "../../assets/result/H_h.svg";
import H_h_unisex from "../../assets/result/H_h_unisex.svg";
import H_c from "../../assets/result/H_c.svg";

import F_c from "../../assets/result/F_c.svg";
import F_cw from "../../assets/result/F_cw.svg";
import F_ctwh from "../../assets/result/F_ctwh.svg";
import F_wh from "../../assets/result/F_wh.svg";
import F_h_unisex from "../../assets/result/F_h_unisex.svg";

import example_tailes from "../../assets/result/example_tailes.png";
import example_tailes_2 from "../../assets/result/example_tailes_2.png";

import { removeLocalStore } from "../../store/localStoreUtils";
import { useUserContext } from "../../store/userContext";
import {
  ReducedResultType,
  SimilarProductType,
  RecommendedSizeType,
} from "../../types/result";
import { GENDERS } from "../../constants/modal";
import ShoeResult from "./components/ShoeResult";
import { capitalizeFirstLetter, useIsMobile } from "../../utils";
import UnavailableGallery from "./components/UnavailableGallery";
import { findSimilarProducts } from "../../api/endpoints";
import { findBrandByDomain } from "../../configs/configLoader";
import { handleAnalytics } from "../../utils/tracking";

import InfoStyledIcon from "../../assets/icons/InfoStyledIcon";

import { logger } from "../../utils/logging";

import {
  postAddToCart,
  postClearMid,
  postCloseIframe,
  postSelectSize,
  postSendMid,
  postSendUid,
} from "../../utils/post";

import GeneratedSizes from "./components/GeneratedSizes";
import DescriptionResults from "./components/DescriptionResults/DescriptionResults";
import SizeSelectorsComponent from "./components/SizeSelectorsComponent";
import { ArrowForward as ChevronRight } from "@mui/icons-material";
import { hoverStylesContinue } from "../../configs/stylesLoader";
import { getSafeStorage } from "../../hooks/useSafeLocalStorage";

import "./index.css";

type SelectorSizeType = "unfocused" | "focused" | "unavailable";

interface IPropsResult {
  step: {
    number: number;
  } | null;
  reducedResult: ReducedResultType;
  selectedGender: string;
  productStockData: any;
  similarProducts: SimilarProductType[] | null;
  isSizeUnavailable: boolean;
  selectedSize: RecommendedSizeType | null;
  setSelectedSize: React.Dispatch<
    React.SetStateAction<RecommendedSizeType | null>
  >;
  setSimilarProducts: React.Dispatch<SimilarProductType[] | null>;
  restart: () => void;
  recommendedSize: RecommendedSizeType[];
}

const Result: FC<IPropsResult> = ({
  step,
  reducedResult,
  selectedGender,
  productStockData,
  similarProducts,
  isSizeUnavailable,
  selectedSize,
  setSelectedSize,
  setSimilarProducts,
  recommendedSize = [],
  restart,
}) => {
  const isMobile = useIsMobile();
  const isVerySmallMobile = useMediaQuery({ maxWidth: 400 });

  const isLargeHeightRelative = useMediaQuery({
    maxHeight: !isMobile ? 830 : 778,
    minWidth: 767,
  });

  const userContext = useUserContext() || undefined;
  const user = userContext?.user;
  const [pid, setPid] = useState<string | null>(userContext?.pid || null);

  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    const localPid = userContext?.pid || null;
    if (localPid) setPid(localPid);
  }, [userContext]);

  const { t } = useTranslation("components/results/result");

  const fit_feedbacks = [t("size.size1"), t("size.size2"), t("size.size3")];

  const [selectedVariant, setSelectedVariant] = useState<string>("1");
  const [selectedSizeImage, setSelectedSizeImage] = useState<string>("");
  const [feedbacks, setFeedbacks] = useState<any>({});
  const [isSimilarProductsLoaded, setIsSimilarProductsLoaded] = useState(false);

  const brandDefined = findBrandByDomain();

  const urlParameters = new URLSearchParams(window.location.search);
  const isFootwear =
    urlParameters.get("category") === "footwear" ||
    urlParameters.get("footwear") === "true";
  const isChildren =
    urlParameters.get("category") === "children" ||
    urlParameters.get("kids") === "true";

  const domain = urlParameters.get("domain");
  const isLingerie = urlParameters.get("lingerie") === "true";
  const lang = urlParameters.get("lang");
  const market = urlParameters.get("market");
  const mock = urlParameters.get("mock") === "true";

  const isDesktopGerardDarel =
    brandDefined?.name === "Gerard Darel" && !isMobile;
  const isSudExpress = brandDefined?.name === "Sud Express";
  const isPeserico = brandDefined?.name === "Peserico";
  const isVB = brandDefined?.name === "Victoria Beckham";
  const isIntersport = brandDefined?.name === "Intersport";
  const isSRP = brandDefined?.name === "SRP";
  const isSoeur = brandDefined?.name === "Soeur";
  const isZadig = brandDefined?.name === "Zadig & Voltaire";
  const isLacoste = brandDefined?.name === "Lacoste";
  const isVelour = brandDefined?.name === "Velour Garments";
  const isWeill = brandDefined?.name === "Weill";
  const isAsphalte = brandDefined?.name === "Asphalte";
  const isGivenchy = brandDefined?.name === "Givenchy";
  const isAntonelle = brandDefined?.name === "Antonelle";
  const isTDF = brandDefined?.name === "TDF";

  const isCustomResultDesign = isVB || isGivenchy;
  const isGivenchyDesktop = isGivenchy && !isMobile;

  const shoeResultScreenRender = isFootwear || isChildren || isLingerie;

  useEffect(() => {
    if (reducedResult) {
      const entries = Object.entries(reducedResult);
      if (entries.length > 1) {
        setSelectedVariant(entries[1][0]);
      }
    }
  }, [reducedResult]);

  const handleFindSimilarProducts = async (
    variant_id: any,
    activeRequestId: any,
  ) => {
    if (!isSizeUnavailable) return;

    const noVariantIdBrands = [
      "Place des Tendances",
      "Rodier",
      "La Canadienne",
    ];
    const variantIsUnavailable = brandDefined?.name
      ? noVariantIdBrands.includes(brandDefined.name)
      : false;

    const variantId = variantIsUnavailable ? null : variant_id;
    const asphalte = domain === "asphalte.com";

    if (domain && !asphalte && pid && productStockData?.length) {
      const currentRequestId = Symbol("requestId");
      activeRequestId = currentRequestId;

      if (!mock) setSimilarProducts([]);
      setIsSimilarProductsLoaded(false);

      try {
        let similarProducts = await findSimilarProducts(
          domain,
          pid,
          variantId ?? null,
        );

        if (Array.isArray(similarProducts)) {
          if (market && domain.includes("zadig")) {
            similarProducts = similarProducts.map((product: any) => {
              return {
                ...product,
                product_url: product.product_url.replace("eu/fr", market),
              };
            });
          }

          if (domain.includes("bonnegueule") && lang === "en") {
            similarProducts = similarProducts.map((product: any) => {
              return {
                ...product,
                product_url: product.product_url.replace(
                  /(https:\/\/[^/]+)(\/.*)/,
                  "$1/en$2",
                ),
              };
            });
          }
        }

        if (
          (activeRequestId === currentRequestId ||
            (isSRP && similarProducts?.length)) &&
          Array.isArray(similarProducts) &&
          similarProducts.length > 0
        ) {
          setSimilarProducts(similarProducts);
        } else {
          if (!mock) setSimilarProducts([]);
        }
      } catch (error) {
        logger.error(`error: ${error}`);
        setIsSimilarProductsLoaded(true);
        if (!mock) setSimilarProducts([]);
      } finally {
        setIsSimilarProductsLoaded(true);
      }
    }
  };

  const allValues = Object.values(reducedResult || {});
  const sorted = allValues.sort((a, b) => a.label_rank - b.label_rank);

  const sizeLabelRank0 = sorted.find((item) => item.label_rank === 0) || null;
  const sizeLabelRank1 = sorted.find((item) => item.label_rank === 1) || null;
  const sizeLabelRank2 = sorted.find((item) => item.label_rank === 2) || null;

  const sizesData = [sizeLabelRank0, sizeLabelRank1, sizeLabelRank2];

  useEffect(() => {
    if (!selectedVariant || !reducedResult || shoeResultScreenRender) return;

    const reducedResultValues = Object.values(reducedResult || {});
    const size = reducedResultValues.find(
      (res) => res.label_rank === Number(selectedVariant),
    );

    if (!size) {
      const defaultSize = reducedResult[0];
      if (defaultSize) setSelectedSize(defaultSize);
    } else {
      setSelectedSize(size);
    }

    const idToSend = isAntonelle ? size?.label : size?.variant_id;

    if (idToSend?.toString().trim()) {
      postSelectSize(idToSend);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariant, reducedResult, shoeResultScreenRender]);

  useEffect(() => {
    if (!selectedSize || !isSizeUnavailable) return;

    let activeRequestId = null;

    handleFindSimilarProducts(selectedSize?.variant_id, activeRequestId);

    return () => {
      activeRequestId = null;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSizeUnavailable, selectedSize]);

  useEffect(() => {
    const focused = selectedVariant ? parseInt(selectedVariant, 10) : null;
    if (reducedResult && focused !== null) {
      const values = Object.values(reducedResult || {});

      const focusedFeeback =
        values.length === 1
          ? reducedResult?.[0]?.fit_indicators
          : reducedResult?.[focused]?.fit_indicators;

      if (focusedFeeback) {
        const result = focusedFeeback?.reduce(
          (acc: any, { limb, value }: any) => {
            acc[limb] = value - 1;
            return acc;
          },
          {},
        );
        setFeedbacks(result);
      }
    }
  }, [reducedResult, selectedVariant]);

  useEffect(() => {
    const gender = selectedGender || localStorage.getItem("gender") || "";

    if (feedbacks) {
      if (gender === GENDERS.M) {
        // result 1 - MALE
        if (
          feedbacks.hip >= 0 &&
          feedbacks.waist >= 0 &&
          feedbacks.chest === undefined
        ) {
          setSelectedSizeImage(H_twh);
        }

        // result 2 - MALE
        if (
          feedbacks.chest >= 0 &&
          feedbacks.waist >= 0 &&
          feedbacks.hip === undefined
        ) {
          setSelectedSizeImage(H_ctw);
        }

        // result 3 - MALE
        if (
          feedbacks.chest >= 0 &&
          feedbacks.waist === undefined &&
          feedbacks.hip === undefined
        ) {
          setSelectedSizeImage(H_c);
        }
      }

      if (gender === GENDERS.F) {
        // result 1 - FEMALE
        if (
          feedbacks.chest >= 0 &&
          feedbacks.waist >= 0 &&
          feedbacks.hip === undefined
        ) {
          setSelectedSizeImage(F_cw);
        }

        // result 2 - FEMALE
        if (
          feedbacks.hip >= 0 &&
          feedbacks.waist >= 0 &&
          feedbacks.chest === undefined
        ) {
          setSelectedSizeImage(F_wh);
        }

        // result 3 - FEMALE
        if (
          feedbacks.chest >= 0 &&
          feedbacks.waist === undefined &&
          feedbacks.hip === undefined
        ) {
          setSelectedSizeImage(F_c);
        }
      }

      // result 3 - MALE
      if (
        feedbacks.waist >= 0 &&
        feedbacks.hip === undefined &&
        feedbacks.chest === undefined
      ) {
        setSelectedSizeImage(H_h);
      }

      // unisex result
      if (
        feedbacks.hip >= 0 &&
        feedbacks.waist === undefined &&
        feedbacks.chest === undefined
      ) {
        setSelectedSizeImage(gender === GENDERS.M ? H_h_unisex : F_h_unisex);
      }

      // result 3 - ALL
      if (feedbacks.chest >= 0 && feedbacks.waist >= 0 && feedbacks.hip >= 0) {
        setSelectedSizeImage(gender === GENDERS.M ? H_ctwh : F_ctwh);
      }
    } else {
      // setSelectedSizeImage(selectedGender === GENDERS.F ? F_cw : H_h);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbacks, selectedGender]);

  const isSRPUnavailable = isSRP && isSizeUnavailable;

  const getSelectorSizeStyles = (type: SelectorSizeType, variant: string) => {
    const sizeStyles = resultScreenUI.sizeSelector[type]!;

    const borderWidthNum = parseInt(sizeStyles.borderWidth);
    const cornerBorderWidth =
      !isSudExpress && variant === "1"
        ? `${borderWidthNum - 1}px`
        : `${borderWidthNum}px`;

    const values = Object.values(reducedResult || {});

    const SRPUnavailableSize = isSRPUnavailable && selectedVariant === variant;

    const selectorSizeStyles = {
      backgroundColor: SRPUnavailableSize
        ? "rgba(39, 38, 38, 0.08)"
        : (sizeStyles.backgroundColor as React.CSSProperties["backgroundColor"]),
      fontWeight: sizeStyles.fontWeight as React.CSSProperties["fontWeight"],
      fontSize: sizeStyles.fontSize as React.CSSProperties["fontSize"],
      textTransform:
        sizeStyles.textTransform as React.CSSProperties["textTransform"],
      color: SRPUnavailableSize
        ? "rgba(39, 38, 38, 0.32)"
        : (sizeStyles.fontColor as React.CSSProperties["color"]),
      borderTopLeftRadius:
        variant === "0"
          ? (sizeStyles.borderRadius as React.CSSProperties["borderRadius"])
          : 0,
      borderBottomLeftRadius:
        variant === "0"
          ? (sizeStyles.borderRadius as React.CSSProperties["borderRadius"])
          : 0,
      borderTopRightRadius:
        variant === "2"
          ? (sizeStyles.borderRadius as React.CSSProperties["borderRadius"])
          : 0,
      borderBottomRightRadius:
        variant === "2"
          ? (sizeStyles.borderRadius as React.CSSProperties["borderRadius"])
          : 0,
      borderWidthStyle: sizeStyles.borderWidth,
      borderTop: `${sizeStyles.borderWidth} solid ${
        SRPUnavailableSize ? "rgba(39, 38, 38, 0.32)" : sizeStyles.borderColor
      }`,
      borderBottom: `${sizeStyles.borderWidth} solid ${
        SRPUnavailableSize ? "rgba(39, 38, 38, 0.32)" : sizeStyles.borderColor
      }`,
      borderLeft:
        selectedSize?.possible === 0 && selectedVariant === "1"
          ? `${cornerBorderWidth} solid ${resultScreenUI.sizeSelector["unavailable"]?.borderColor}`
          : isSRPUnavailable
            ? `${cornerBorderWidth} solid rgba(2, 1, 1, 0.32)`
            : `${cornerBorderWidth} solid ${
                values.length === 1
                  ? sizeStyles.borderColor
                  : selectedVariant === "1" && variant === "2"
                    ? resultScreenUI.sizeSelector["focused"]?.borderColor
                    : sizeStyles.borderColor
              }`,
      borderRight:
        selectedSize?.possible === 0 && selectedVariant === "1"
          ? `${cornerBorderWidth} solid ${resultScreenUI.sizeSelector["unavailable"]?.borderColor}`
          : isSRPUnavailable
            ? `${cornerBorderWidth} solid rgba(39, 38, 38, 0.32)`
            : `${cornerBorderWidth} solid ${
                values.length === 1
                  ? sizeStyles.borderColor
                  : selectedVariant === "1" && variant === "0"
                    ? resultScreenUI.sizeSelector["focused"]?.borderColor
                    : sizeStyles.borderColor
              }`,
      letterSpacing: (sizeStyles as any)?.letterSpacing,
      fontStyle: "normal",
    };

    return selectorSizeStyles;
  };

  const firstSelectorStyles =
    selectedVariant === "0"
      ? getSelectorSizeStyles(
          selectedSize?.possible === 0 && !isSizeUnavailable
            ? "unavailable"
            : "focused",
          "0",
        )
      : getSelectorSizeStyles("unfocused", "0");

  const secondSelectorStyles =
    selectedVariant === "1"
      ? getSelectorSizeStyles(
          selectedSize?.possible === 0 ? "unavailable" : "focused",
          "1",
        )
      : getSelectorSizeStyles("unfocused", "1");

  const secondSelectorIdealStyles =
    selectedVariant === "1"
      ? getSelectorSizeStyles("focused", "1")
      : getSelectorSizeStyles("unfocused", "1");

  const thirdSelectorStyles =
    selectedVariant === "2"
      ? getSelectorSizeStyles(
          selectedSize?.possible === 0 && !isSizeUnavailable
            ? "unavailable"
            : "focused",
          "2",
        )
      : getSelectorSizeStyles("unfocused", "2");

  const skipTextTransform = resultScreenUI.restartCTA
    .textTransform as React.CSSProperties["textTransform"];

  const sendIds = () => {
    const uid = getSafeStorage().getItem("uid");
    const mid = getSafeStorage().getItem("mid");
    postSendMid(mid);
    if (uid && uid !== null && uid !== undefined) {
      postSendUid(uid);
    }
  };
  const clearMid = () => {
    postClearMid();
  };

  const addToCart = () => {
    let variantId = selectedSize?.variant_id;
    logger.log(`addToCart called: ${variantId}`);
    if (variantId) postAddToCart(variantId);
  };

  const isShowSimilarProducts =
    similarProducts?.length && isSizeUnavailable && !isSRP && !isAsphalte;

  const getMarginTopSkip = () => {
    if (isGivenchyDesktop && isShowSimilarProducts) return "40px";
    if (!isMobile && isSoeur) return "20px";
    if (isDesktopGerardDarel) return "10px";
    if (isLargeHeightRelative) return "20px";
    if (isMobile) return "0px";

    return "20px";
  };

  const CTAStyles: React.CSSProperties = {
    ...activeStylesContinue,
    ...(hovered ? hoverStylesContinue : {}),
    textTransform:
      activeStylesContinue.textTransform === "capitalize"
        ? "none"
        : activeStylesContinue.textTransform,
    letterSpacing: (activeStylesContinue as any)?.letterSpacing,
    padding: isTDF ? "0" : isPeserico ? "12px 16px" : isVB ? "7px" : "16px",
    height: isTDF ? "28px" : isVB ? "30px" : "auto",
    maxHeight: isTDF ? "28px" : isGivenchy ? "40px" : "fit-content",
    position: isGivenchyDesktop ? "fixed" : "relative",
    bottom: isGivenchyDesktop ? "195px" : 0,
    width: isGivenchyDesktop ? "calc(100% - 160px)" : "",
  };

  const arrowContainerStyle: React.CSSProperties = {
    display: "inline-block",
    width: hovered && selectedSize ? 16 : 0,
    overflow: "hidden",
    verticalAlign: "middle",
    transition: "width 160ms ease",
  };

  const iconStyle: React.CSSProperties = {
    width: 16,
    height: 16,
    opacity: hovered && selectedSize ? 1 : 0,
    transform: hovered && selectedSize ? "translateX(0)" : "translateX(-6px)",
    transition: "opacity 160ms ease, transform 160ms ease",
    display: "inline-block",
    verticalAlign: "middle",
    pointerEvents: "none",
  };

  const handleNextSelection = () => {
    if (selectedSize?.variant_id) {
      addToCart();
      sendIds();
      postCloseIframe();

      handleAnalytics("action", "size_atc", {
        key: "value",
        value: selectedSize?.label,
      });
    }
  };

  return shoeResultScreenRender ? (
    <ShoeResult
      step={step}
      restart={restart}
      recommendation={recommendedSize}
      selectedVariant={selectedVariant}
      setSelectedVariant={setSelectedVariant}
      isSizeUnavailable={isSizeUnavailable}
      selectedSize={selectedSize}
      setSelectedSize={setSelectedSize}
      similarProducts={similarProducts}
    />
  ) : (
    <div
      className="result"
      style={{
        marginTop: isLargeHeightRelative ? "32px" : !isMobile ? "40px" : 0,
        paddingBottom:
          isGivenchyDesktop && isShowSimilarProducts ? "40px" : "0px",
      }}
    >
      {!isCustomResultDesign ? (
        <>
          <div className="size">
            <h1
              className={isTDF ? "millerBannerFont" : ""}
              style={{
                color:
                  isSizeUnavailable && !isSRP
                    ? (resultScreenUI.unavailable_size_text
                        .fontColor as React.CSSProperties["color"])
                    : (resultScreenUI.recommendedSize
                        .fontColor as React.CSSProperties["color"]),
                fontSize: isLargeHeightRelative
                  ? `calc(${resultScreenUI.recommendedSize.fontSize} - 25px)`
                  : !isLacoste && isMobile
                    ? "40px"
                    : (resultScreenUI.recommendedSize
                        .fontSize as React.CSSProperties["fontSize"]),
                fontWeight: isTDF
                  ? 300
                  : (resultScreenUI.recommendedSize
                      .fontWeight as React.CSSProperties["fontWeight"]),
                textAlign: resultScreenUI.recommendedSize
                  .textAlign as React.CSSProperties["textAlign"],
                marginTop: isDesktopGerardDarel
                  ? 0
                  : isLargeHeightRelative
                    ? 0
                    : isMobile
                      ? "10px"
                      : "54px",
                marginBottom: isTDF
                  ? 0
                  : isLargeHeightRelative
                    ? 0
                    : isVerySmallMobile
                      ? "5px"
                      : isMobile
                        ? "10px"
                        : "12px",
                letterSpacing: (resultScreenUI.Description as any)
                  ?.letterSpacing,
                position: "relative",
                top: isTDF ? "20px" : "",
              }}
            >
              {selectedSize?.label || ""}
            </h1>
            <div
              style={{
                position: "absolute",
                width: "1px",
                height: "1px",
                overflow: "hidden",
                clip: "rect(0 0 0 0)",
              }}
            >
              {selectedSize
                ? t("aria.recommended_size", { size: selectedSize.label })
                : ""}
            </div>
            <div className="size-name">
              {selectedVariant === "1" && !isTDF && (
                <>
                  <h2
                    style={{
                      color: isSRPUnavailable
                        ? "rgba(39, 38, 38, 0.32)"
                        : ((secondSelectorIdealStyles.color === "#FFFFFF"
                            ? secondSelectorIdealStyles.backgroundColor
                            : secondSelectorIdealStyles.color) as React.CSSProperties["color"]),
                      fontSize: resultScreenUI.subtitles
                        .fontSize as React.CSSProperties["fontSize"],
                      fontWeight:
                        isZadig || isLacoste
                          ? 700
                          : (resultScreenUI.subtitles
                              .fontWeight as React.CSSProperties["fontWeight"]),
                      textAlign: resultScreenUI.subtitles
                        .textAlign as React.CSSProperties["textAlign"],
                    }}
                  >
                    {t("size.title").replace(
                      "[S]",
                      sizeLabelRank1?.label || "",
                    )}
                  </h2>
                  {!isZadig && !isLacoste && (
                    <>
                      {isSRPUnavailable ? (
                        <CheckIconUnavailable
                          aria-hidden="true"
                          focusable="false"
                          fill={
                            (secondSelectorIdealStyles.color === "#FFFFFF"
                              ? secondSelectorIdealStyles.backgroundColor
                              : secondSelectorIdealStyles.color) as React.CSSProperties["color"]
                          }
                        />
                      ) : (
                        <CheckCircleIcon
                          aria-hidden="true"
                          focusable="false"
                          style={{
                            color: (secondSelectorIdealStyles.color ===
                            "#FFFFFF"
                              ? secondSelectorIdealStyles.backgroundColor
                              : secondSelectorIdealStyles.color) as React.CSSProperties["color"],
                          }}
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </div>
            {!isLingerie ? (
              <SizeSelectorsComponent
                sizeLabelRank0={sizeLabelRank0}
                sizeLabelRank1={sizeLabelRank1}
                sizeLabelRank2={sizeLabelRank2}
                selectedVariant={selectedVariant}
                setSelectedVariant={setSelectedVariant}
                firstSelectorStyles={firstSelectorStyles}
                secondSelectorStyles={secondSelectorStyles}
                thirdSelectorStyles={thirdSelectorStyles}
                fit_feedbacks={fit_feedbacks}
                selectedSize={selectedSize}
                isSizeUnavailable={isSizeUnavailable}
                recommendedSize={recommendedSize}
                sizesData={sizesData}
              />
            ) : null}
          </div>
        </>
      ) : (
        <div className="size">
          {!isLingerie ? (
            <SizeSelectorsComponent
              sizeLabelRank0={sizeLabelRank0}
              sizeLabelRank1={sizeLabelRank1}
              sizeLabelRank2={sizeLabelRank2}
              selectedVariant={selectedVariant}
              setSelectedVariant={setSelectedVariant}
              firstSelectorStyles={firstSelectorStyles}
              secondSelectorStyles={secondSelectorStyles}
              thirdSelectorStyles={thirdSelectorStyles}
              fit_feedbacks={fit_feedbacks}
              selectedSize={selectedSize}
              isSizeUnavailable={isSizeUnavailable}
              recommendedSize={recommendedSize}
              sizesData={sizesData}
            />
          ) : null}
        </div>
      )}

      {!isVelour && !isVB ? (
        <>
          {resultScreenUI.generateResult && !isLingerie ? (
            <GeneratedSizes
              selectedSizeImage={selectedSizeImage}
              feedbacks={feedbacks}
              selectedVariant={selectedVariant}
              fit_feedbacks={fit_feedbacks}
              selectedSize={selectedSize}
            />
          ) : (
            <DescriptionResults
              isSizeUnavailable={isSizeUnavailable}
              selectedVariant={selectedVariant}
              selectedSize={selectedSize}
              similarProducts={similarProducts}
              isSimilarProductsLoaded={isSimilarProductsLoaded}
            />
          )}
        </>
      ) : null}

      {isVelour && (
        <div
          className="result-body"
          tabIndex={0}
          aria-labelledby="result-description-title"
          style={{ borderColor: "transparent", height: "40px" }}
        ></div>
      )}

      {isLacoste && (
        <div
          className="result-body"
          tabIndex={0}
          aria-labelledby="result-description-title"
          style={{
            height: "auto",
            padding: isMobile ? "10px 0" : "20px 0",
            display: "flex",
            flexDirection: "column",
            gap: "5px",
            marginBottom: "10px",
            border: "1px #E9E9E9 solid",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "10px",
              alignItems: "center",
              padding: "0 20px",
            }}
          >
            <InfoStyledIcon
              style={{
                fontSize: "16px",
              }}
              fill={
                resultScreenUI.Description
                  .fontColor as React.CSSProperties["color"]
              }
            />
            <h3
              style={{
                margin: 0,
                fontSize: isVerySmallMobile ? "11px" : "15px",
                fontWeight: 700,
                color: resultScreenUI.Description
                  .fontColor as React.CSSProperties["color"],
                letterSpacing: (resultScreenUI.Description as any)
                  ?.letterSpacing,
              }}
            >
              {t("size_parameter_title").replace(
                "[S]",
                sizeLabelRank1?.label || "",
              )}
            </h3>
          </div>
          <p
            style={{
              margin: 0,
              paddingLeft: "45px",
              paddingRight: isMobile ? "10px" : "50px",
              fontSize: isVerySmallMobile ? "11px" : "15px",
              fontWeight: 500,
              color: resultScreenUI.Description
                .fontColor as React.CSSProperties["color"],
              letterSpacing: (resultScreenUI.Description as any)?.letterSpacing,
            }}
          >
            {t("size_parameter_description").replace(
              "[S]",
              sizeLabelRank1?.label || "",
            )}
          </p>
        </div>
      )}
      {/* if need to use for SRP use <UnavailableGallerySRP ... /> component  */}
      {isShowSimilarProducts ? (
        <UnavailableGallery similarProducts={similarProducts} />
      ) : null}
      {selectedSize && selectedSize?.variant_id && !isSizeUnavailable && (
        <button
          tabIndex={0}
          type="button"
          aria-label={t("button").replace(
            "[S]",
            selectedSize?.label?.toUpperCase() || "",
          )}
          className={`continue-button step-bottom-result`}
          onMouseEnter={(e) => {
            if (isMobile) return;
            setHovered(true);
            Object.assign(e.currentTarget.style, hoverStylesContinue);
          }}
          onMouseLeave={(e) => {
            setHovered(false);
            Object.assign(e.currentTarget.style, CTAStyles);
          }}
          onClick={() => handleNextSelection()}
          style={CTAStyles}
        >
          <div style={{ display: "inline-flex", alignItems: "center", gap: 0 }}>
            {t("button").replace(
              "[S]",
              selectedSize?.label?.toUpperCase() || "",
            )}

            {isIntersport && (
              <span style={arrowContainerStyle} aria-hidden>
                <ChevronRight
                  aria-hidden="true"
                  focusable="false"
                  style={iconStyle}
                />
              </span>
            )}
          </div>
        </button>
      )}
      <span
        role="button"
        tabIndex={0}
        aria-label={t("skip")}
        className="skip"
        onClick={() => {
          clearMid();
          restart();

          handleAnalytics("action", "result_restart", null);

          removeLocalStore("mid");
          removeLocalStore("user");
          if (!uxGender) removeLocalStore("gender");

          if (localStorage.getItem("user")) {
            localStorage.setItem("user", JSON.stringify(user));
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.currentTarget.click();
          }
        }}
        style={{
          backgroundColor: isSRP
            ? "transparent"
            : (resultScreenUI.restartCTA
                .backgroundColor as React.CSSProperties["backgroundColor"]),
          fontWeight: resultScreenUI.restartCTA
            .fontWeight as React.CSSProperties["fontWeight"],
          fontSize:
            isMobile && isSRP
              ? "12px"
              : (resultScreenUI.restartCTA
                  .fontSize as React.CSSProperties["fontSize"]),
          letterSpacing: (resultScreenUI.restartCTA as any)
            ?.letterSpacing as React.CSSProperties["letterSpacing"],
          lineHeight: (resultScreenUI.restartCTA as any)
            ?.lineHeight as React.CSSProperties["lineHeight"],
          color: resultScreenUI.restartCTA
            .fontColor as React.CSSProperties["color"],
          textTransform:
            skipTextTransform === "capitalize" ? "none" : skipTextTransform,
          borderRadius: resultScreenUI.restartCTA
            .borderRadius as React.CSSProperties["borderRadius"],
          border: resultScreenUI.restartCTA.borderWidth
            ? `${resultScreenUI.restartCTA.borderWidth} solid ${
                isGivenchy && !isShowSimilarProducts
                  ? "transparent"
                  : resultScreenUI.restartCTA.borderColor
              }`
            : "none",
          textDecoration: resultScreenUI.restartCTA
            .textDecoration as React.CSSProperties["borderRadius"],
          alignItems: "center",
          marginBottom:
            isGivenchyDesktop && isShowSimilarProducts
              ? "50px"
              : isMobile
                ? "20px"
                : !isMobile
                  ? "50px "
                  : 0,
          marginTop: getMarginTopSkip(),
          fontStyle: "normal",
          position:
            isGivenchyDesktop && !isShowSimilarProducts ? "fixed" : "relative",
          bottom: isGivenchyDesktop && !isShowSimilarProducts ? "95px" : 0,
          width:
            isGivenchyDesktop && !isShowSimilarProducts
              ? "calc(100% - 160px)"
              : "",
          padding:
            isGivenchy || isTDF
              ? "0px"
              : resultScreenUI.restartCTA.borderWidth
                ? "12px"
                : "0px",
          height: isGivenchy ? "40px" : isTDF ? "28px" : isVB ? "30px" : "auto",
          maxHeight: isTDF ? "28px" : isGivenchy ? "38px" : "fit-content",
        }}
      >
        {skipTextTransform === "capitalize"
          ? capitalizeFirstLetter(t("skip"))
          : t("skip")}
      </span>
      {(isSoeur || isWeill) && !isSizeUnavailable && (
        <img
          src={isWeill ? example_tailes_2 : example_tailes}
          alt=""
          role="presentation"
          style={{
            position: "relative",
            width: "100%",
            height: "auto",
            marginBottom: !isMobile ? "70px" : 0,
          }}
        />
      )}
      {!isLacoste && <MeasuredBy step={step} />}
    </div>
  );
};

export default Result;
